import { useState, useRef, useEffect } from 'react';
import { useAiAssistantStore } from '@/store/ai-assistant.store';
import { useAiQuery } from '@/hooks/useAiAssistant';
import { useIncidentStore } from '@/store/incident.store';
import AiAssistantMessage from './AiAssistantMessage';
import Button, { IconButton } from '@/components/common/Button';
import FaIcon from '@/components/common/FaIcon';
import { PaperAirplaneIcon, TrashIcon } from '../icons';

export default function AiAssistantPanel() {
  const {
    isPanelOpen,
    setPanelOpen,
    messages,
    isLoading,
    addMessage,
    setLoading,
    clearMessages,
  } = useAiAssistantStore();

  const aiQuery = useAiQuery();
  const { activeIncident } = useIncidentStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isPanelOpen]);

  // Close on escape
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isPanelOpen) {
        setPanelOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPanelOpen, setPanelOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = input.trim();
    if (!query || isLoading) return;

    setInput('');

    // Add user message
    addMessage({
      id: 'user-' + Date.now(),
      role: 'user',
      content: query,
      timestamp: new Date().toISOString(),
    });

    setLoading(true);

    try {
      const activeIncidentId = activeIncident?.id;
      const result = await aiQuery.mutateAsync({
        query,
        incidentId: activeIncidentId || undefined,
      });

      addMessage({
        id: 'ai-' + Date.now(),
        role: 'assistant',
        content: result.answer,
        data_table: result.data_table || undefined,
        references: result.references || undefined,
        sources_queried: result.sources_queried,
        timestamp: new Date().toISOString(),
      });
    } catch {
      addMessage({
        id: 'error-' + Date.now(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isPanelOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        onClick={() => setPanelOpen(false)}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="AI Assistant"
        tabIndex={-1}
        className="fixed right-0 top-0 h-full w-full sm:w-96 md:w-[420px] bg-surface-elevated shadow-xl z-50 flex flex-col"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-primary">
          <div className="flex items-center gap-2">
            <FaIcon icon="sparkles" className="h-5 w-5 text-purple-500" />
            <h2 className="heading-3">AI Assistant</h2>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <IconButton
                icon={<TrashIcon className="h-4 w-4" />}
                label="Clear conversation"
                variant="ghost"
                size="sm"
                onClick={clearMessages}
              />
            )}
            <IconButton
              icon={<FaIcon icon="xmark" className="h-5 w-5" />}
              label="Close panel"
              variant="ghost"
              size="sm"
              onClick={() => setPanelOpen(false)}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          {messages.length === 0 && (
            <div className="text-center text-theme-tertiary mt-12">
              <FaIcon icon="sparkles" className="h-12 w-12 mx-auto mb-3 text-purple-300 dark:text-purple-600" />
              <p className="font-medium">Ask me anything about your incident</p>
              <p className="text-sm mt-1">I can help with tasks, damage assessments, shelters, supply requests, and more.</p>
              <div className="mt-4 space-y-2">
                {[
                  'What is the current status of all tasks?',
                  'How many people are displaced?',
                  'Summarize damage assessments by severity',
                  'What supply requests are pending?',
                ].map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setInput(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="block w-full text-left px-3 py-2 text-sm h-auto justify-start"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <AiAssistantMessage key={msg.id} message={msg} />
          ))}

          {isLoading && (
            <div className="flex justify-start mb-3">
              <div className="bg-surface-tertiary rounded-lg px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span className="text-sm text-theme-tertiary">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="border-t border-theme-primary p-3">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your incident..."
              // The panel heading supplies context visually, but this control has no label of its
              // own — and a placeholder is not one, disappearing the moment the operator types.
              aria-label="Ask the AI assistant about your incident"
              disabled={isLoading}
              className="flex-1 px-3 py-2 rounded-lg border border-theme-secondary bg-surface-primary text-theme-primary text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              icon={<PaperAirplaneIcon className="h-4 w-4" />}
              disabled={!input.trim() || isLoading}
              className="bg-purple-600 hover:bg-purple-700"
              aria-label="Send message"
              title="Send message"
            />
          </div>
        </form>
      </div>
    </>
  );
}
